/**
 * InvestmentFormDialog — create/edit modal for an Investment.
 *
 * Extracted from Investments.jsx (iter-103) to keep the parent page focused
 * on listing + actions. All state lives on the parent and is passed in;
 * this component is intentionally dumb so the test surface stays small.
 *
 * Side-effect contract:
 *   • DCA fields (account + first date) only become enabled once
 *     `form.contribution_amount` > 0. The parent runs the hard validation
 *     when frequency is set.
 *   • Currency selector defaults to the user's profile currency, but any
 *     entry in `fxList` is valid. The submit payload is normalised in the
 *     parent (`save()`).
 */
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { BottomSheet } from "../../../components/ui/bottom-sheet";
import { DatePicker } from "../../../components/DatePicker";
import { AccountPickerField } from "../../../components/AccountPickerField";
import { INVESTMENT_ICONS, pickIcon } from "../../../lib/typeIcons";

export function InvestmentFormDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  currency,
  fxList,
  cashAccounts,
  onSave,
  lang,
  t,
  kinds,
  contribFreqs,
  fieldErrors = {},
  setFieldErrors,
}) {
  const isEs = lang === "es";
  const update = (key, value) => {
    setForm({ ...form, [key]: value });
    if (fieldErrors[key] && setFieldErrors) setFieldErrors({ ...fieldErrors, [key]: undefined });
  };
  const errCls = (k) => fieldErrors[k] ? "border-rose-300 focus-visible:ring-rose-300/40" : "";
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      testid="investment-dialog"
      size="lg"
      title={editing ? (isEs ? "Editar" : "Edit") : (isEs ? "Nueva inversión" : "New investment")}
      description={isEs ? "Captura los datos de tu inversión" : "Capture your investment details"}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-11">{t("cancel")}</Button>
          <Button data-testid="inv-form-save" onClick={onSave} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{t("save")}</Button>
        </div>
      }
    >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <Label htmlFor="inv-form-name" className="flex items-center gap-1">
                {isEs ? "Nombre" : "Name"}
                <span className="text-rose-500" aria-hidden="true">*</span>
              </Label>
              <Input
                id="inv-form-name"
                data-testid="inv-form-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Bitcoin / Apple / S&P 500"
                aria-invalid={fieldErrors.name ? "true" : undefined}
                className={errCls("name")}
              />
              {fieldErrors.name && (
                <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="inv-form-name-error">{fieldErrors.name}</p>
              )}
            </div>
            <div>
              <Label>{isEs ? "Tipo" : "Type"}</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="inv-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {kinds.map((k) => {
                    const KIcon = pickIcon(INVESTMENT_ICONS, k.v);
                    return (
                      <SelectItem key={k.v} value={k.v}>
                        <span className="inline-flex items-center gap-2">
                          <KIcon className="h-4 w-4 text-slate-500" />
                          {isEs ? k.labelEs : k.labelEn}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ticker</Label>
              <Input data-testid="inv-form-ticker" value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value })} placeholder="BTC / AAPL" />
            </div>
            <div>
              <Label htmlFor="inv-form-invested" className="flex items-center gap-1">
                {isEs ? "Monto invertido" : "Amount invested"}
                <span className="text-rose-500" aria-hidden="true">*</span>
              </Label>
              <Input
                id="inv-form-invested"
                data-testid="inv-form-invested"
                type="number"
                value={form.amount_invested}
                onChange={(e) => update("amount_invested", e.target.value)}
                aria-invalid={fieldErrors.amount_invested ? "true" : undefined}
                className={errCls("amount_invested")}
              />
              {fieldErrors.amount_invested && (
                <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="inv-form-invested-error">{fieldErrors.amount_invested}</p>
              )}
            </div>
            <div>
              <Label>{isEs ? "Valor actual" : "Current value"}</Label>
              <Input data-testid="inv-form-value" type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>{isEs ? "Fecha de compra" : "Purchase date"}</Label>
              <DatePicker
                testid="inv-form-date"
                value={form.purchase_date}
                onChange={(v) => setForm({ ...form, purchase_date: v })}
                placeholder={isEs ? "Selecciona fecha" : "Pick a date"}
              />
            </div>
            <div>
              <Label>{isEs ? "Moneda" : "Currency"}</Label>
              <Select
                value={form.currency || currency}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger data-testid="inv-form-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(fxList.length ? fxList : [currency]).map((c) => (
                    <SelectItem key={c} value={c}>{c}{c === currency ? ` · ${isEs ? "principal" : "primary"}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isEs ? "Rendimiento anual esperado (%)" : "Expected annual return (%)"}</Label>
              <Input
                data-testid="inv-form-rate"
                type="number"
                step="0.1"
                value={form.expected_annual_return}
                onChange={(e) => setForm({ ...form, expected_annual_return: e.target.value })}
                placeholder="6.5"
              />
            </div>
            <div className="col-span-2">
              <Label>{isEs ? "Fecha de retiro (opcional)" : "Withdrawal date (optional)"}</Label>
              <DatePicker
                testid="inv-form-withdrawal-date"
                value={form.withdrawal_date}
                onChange={(v) => setForm({ ...form, withdrawal_date: v })}
                placeholder={isEs ? "Selecciona fecha" : "Pick a date"}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {isEs
                  ? "Si tiene fecha, se crea un Cobro automático en /Cobros al vencimiento."
                  : "When set, an auto-Receivable is created in /Receivables at maturity."}
              </p>
            </div>
            {/* DCA / aportes periódicos — opcional. Cuando se setean, se
                materializa un Pago recurrente en /Pagos que al marcarlo como
                pagado debita la cuenta y suma a amount_invested + current_value. */}
            <div>
              <Label>{isEs ? "Aporte recurrente (opcional)" : "Recurring contribution (optional)"}</Label>
              <Input
                data-testid="inv-form-contrib-amount"
                type="number"
                step="0.01"
                value={form.contribution_amount}
                onChange={(e) => setForm({ ...form, contribution_amount: e.target.value })}
                placeholder="0"
              />
            </div>
            <div>
              <Label>{isEs ? "Frecuencia del aporte" : "Contribution frequency"}</Label>
              <Select
                value={form.contribution_frequency || "_none"}
                onValueChange={(v) => setForm({ ...form, contribution_frequency: v === "_none" ? "" : v })}
              >
                <SelectTrigger data-testid="inv-form-contrib-freq"><SelectValue placeholder={isEs ? "Sin aporte" : "None"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{isEs ? "Sin aporte" : "None"}</SelectItem>
                  {contribFreqs.map((f) => (
                    <SelectItem key={f.v} value={f.v}>{isEs ? f.es : f.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* DCA funding fields — only meaningful when contribution_amount
                is set. Without account_id the mark_paid handler falls back to
                the primary account; without start_date the upsert defaults
                to purchase_date / today. Showing them always (just disabled
                when no contribution) keeps the layout stable. */}
            <div>
              <AccountPickerField
                label={isEs ? "Cuenta del aporte" : "Contribution account"}
                name="inv-form-contrib-account"
                accounts={cashAccounts}
                value={form.contribution_account_id}
                onChange={(v) => { setForm({ ...form, contribution_account_id: v }); if (fieldErrors.contribution_account_id && setFieldErrors) setFieldErrors({ ...fieldErrors, contribution_account_id: undefined }); }}
                primaryOption={{ label: isEs ? "Cuenta principal" : "Primary account" }}
                error={fieldErrors.contribution_account_id}
                isEs={isEs}
                testid="inv-form-contrib-account-field"
              />
            </div>
            <div>
              <Label className={fieldErrors.contribution_start_date ? "text-rose-600" : ""}>
                {isEs ? "Fecha del primer aporte" : "First contribution date"}
              </Label>
              <DatePicker
                testid="inv-form-contrib-start-date"
                value={form.contribution_start_date}
                onChange={(v) => { setForm({ ...form, contribution_start_date: v }); if (fieldErrors.contribution_start_date && setFieldErrors) setFieldErrors({ ...fieldErrors, contribution_start_date: undefined }); }}
                placeholder={isEs ? "Selecciona fecha" : "Pick a date"}
                disabled={!form.contribution_amount}
              />
              {fieldErrors.contribution_start_date && (
                <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="inv-form-contrib-start-date-error">{fieldErrors.contribution_start_date}</p>
              )}
            </div>
            <div className="col-span-2">
              <Label>{isEs ? "Notas" : "Notes"}</Label>
              <Textarea data-testid="inv-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
        </div>
    </BottomSheet>
  );
}
